import { useState } from "react";
import { motion } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { IMAGES } from "@/lib/images";
import { PageHero, SectionTitle } from "@/components/Shared";
import Lightbox from "@/components/Lightbox";
import ShareButton from "@/components/ShareButton";
import Reviews from "@/components/Reviews";
import { Car, Wine, Disc3, DoorClosed, Cigarette, Sparkles } from "lucide-react";

const AMBIENTI = [
  { icon: Wine, title: "Bar Lounge", desc: "Cocktail e drink raffinati serviti in un'atmosfera intima e curata." },
  { icon: Disc3, title: "Sala Disco", desc: "Pista da ballo con dj set e luci soffuse per notti indimenticabili." },
  { icon: DoorClosed, title: "Stanze a Tema", desc: "Ambienti privé eleganti e riservati, pensati per il massimo comfort." },
  { icon: Car, title: "Parcheggio Riservato", desc: "Ampio parcheggio privato per garantire discrezione e sicurezza." },
  { icon: Cigarette, title: "Area Fumatori", desc: "Spazio dedicato dove rilassarsi in totale libertà." },
  { icon: Sparkles, title: "Privé Room", desc: "Salotti esclusivi immersi in luci calde e velluti bordeaux." },
];

export default function ClubPrive() {
  const { events, settings } = useSite();
  const clubEvents = events.filter((e) => e.category === "club");
  const clubItems = clubEvents.map((e) => ({ id: e.id, image_url: e.image_url || IMAGES.club_red, caption: e.title }));
  const [lbIndex, setLbIndex] = useState(null);

  return (
    <div>
      <PageHero
        overline="Le Notti di Afrodite"
        title="Club Privé"
        subtitle={settings.club_intro}
        image={IMAGES.hero_club}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <SectionTitle overline="Il Programma" title="Le Serate del Club" />
        <p className="text-center text-sm text-[#A39E9B] max-w-xl mx-auto -mt-8 mb-12">
          Le nostre serate si tengono ogni <span className="text-[#D4AF37]">Mercoledì, Venerdì e Sabato</span>.
        </p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-sm sm:max-w-none mx-auto">
          {clubEvents.map((ev, i) => (
            <motion.article
              key={ev.id}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.6 }}
              className="group relative rounded-2xl overflow-hidden border border-[#D4AF37]/15 bg-[#141217]"
            >
              <div onClick={() => setLbIndex(i)} data-testid={`club-event-img-${ev.id}`} className="relative aspect-[9/16] overflow-hidden cursor-pointer bg-black">
                <img src={ev.image_url || IMAGES.club_red} alt={ev.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(180deg, transparent, rgba(20,18,23,0.95))" }} />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#7A1426] text-[#F3E5AB] text-xs font-semibold tracking-wide">
                  {ev.day}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl text-[#FBF8F5] mb-2">{ev.title}</h3>
                {ev.theme && <p className="text-sm text-[#D4AF37] mb-3">{ev.theme}</p>}
                <p className="text-sm text-[#A39E9B] leading-relaxed mb-4">{ev.info}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-[#6E6865]">
                  {ev.dress_code && <span>Dress code: <span className="text-[#A39E9B]">{ev.dress_code}</span></span>}
                  <ShareButton className="ml-auto" testid={`club-share-${ev.id}`} title={ev.title} when={`Ogni ${ev.day}`} image={ev.image_url} />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <SectionTitle overline="Gli Spazi" title="I Nostri Ambienti" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {AMBIENTI.map((a, i) => (
            <motion.div
              key={a.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="p-7 rounded-2xl border border-[#D4AF37]/12 bg-[#141217] hover:border-[#D4AF37]/35 transition-colors duration-500"
            >
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-5">
                <a.icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <h3 className="font-display text-xl text-[#FBF8F5] mb-2">{a.title}</h3>
              <p className="text-sm text-[#A39E9B] leading-relaxed">{a.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Reviews category="club" />

      <Lightbox items={clubItems} index={lbIndex} setIndex={setLbIndex} />
    </div>
  );
}
